import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  setTwoFactorMethod,
  saveTwoFactorSettings,
  resetTwoFactorSettings,
  setOtp,
  verifyOTP,
  setQrCodeData,
  setCurrentMethod,
  setEmail, // Add import
} from "../redux/slices/walletSettingsSlice";
import { RootState } from "../redux/store";
import { sendOtp, verifyOtp, getUser2fa, deactivatesMfa, requestCode, changePrimary } from "service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MfaResponse, MfaResult } from "types";

const useWalletSettings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { twoFactorMethod, settingsStatus, otp, otpverified, qrCodeData, currentMethod, email } = useSelector(
    (state: RootState) => state.walletSettings // Get email from Redux
  );
  const [selectedMethods, setSelectedMethods] = useState<{
    email?: boolean;
    authenticator?: boolean;
  }>(twoFactorMethod);
  const [searchParams] = useSearchParams();
  const currentsetup = searchParams.get("currentSetup");
  const [lastResendTime, setLastResendTime] = useState<number | null>(null);
  const [cooldownSeconds, setCooldownSeconds] = useState<number>(0);

  const { data: mfaDet, isLoading: isLoadingMfa } = useQuery<MfaResponse>({
    queryKey: ["mfa"],
    queryFn: async () => {
      const response = await getUser2fa();
      return response;
    },
  });

  // Update cooldown timer
  useEffect(() => {
    if (!lastResendTime) {
      setCooldownSeconds(0);
      return;
    }

    const updateCooldown = () => {
      const now = Date.now();
      const secondsLeft = Math.max(0, Math.ceil((lastResendTime + 50 * 1000 - now) / 1000));
      setCooldownSeconds(secondsLeft);
    };

    updateCooldown();
    const interval = setInterval(updateCooldown, 1000);

    return () => clearInterval(interval);
  }, [lastResendTime]);

  useEffect(() => {
    if (!mfaDet) return;
    const primary = mfaDet.results.find((mfa: MfaResult) => mfa.is_primary)?.name;
    setSelectedMethods({
      email: primary === "email",
      authenticator: primary === "app",
    });
  }, [mfaDet]);

  // Mutation for sending OTP
  const { mutate: sendOtpMutation, isPending: isSendingOtp } = useMutation({
    mutationFn: async ({ method, payload }: { method: string; payload: Record<string, any> }) => {
      return await sendOtp(method, payload);
    },
    onSuccess: async (data, variables) => {
      if (variables.method === "email") {
        toast.success(`Verification code sent to ${variables.payload.email}`);
        navigate("/wallet/settings/two-factor-setup/provide-otp?currentSetup=email");
      } else if (variables.method === "app") {
        dispatch(setQrCodeData(data.details));
        toast.success("QR code generated for authenticator app");
        navigate("/wallet/settings/two-factor-setup/provide-qr-code?currentSetup=authenticator");
      }
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to send verification code");
    },
  });

  // Mutation for primary MFA
  const { mutate: changePrimaryMfaMutation, isPending: ischangingPrimaryMfaMutation } = useMutation({
    mutationFn: async ({ method, code }: { method: "app" | "email"; code: string }) => {
      return await changePrimary(method, code);
    },
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["mfa"], refetchType: "active" });
      navigate("/wallet/settings/two-factor-setup/");
      if (variables.method === "email") {
        toast.success(`Email has been set for two-factor authentication`);
      } else if (variables.method === "app") {
        toast.success("Authenticator app has been set for authentication");
      }
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to set as authenticated method");
    },
  });

  // Mutation for requesting OTP
  const { mutate: requestOtpMutation, isPending: isRequestingOtp } = useMutation({
    mutationFn: async ({ method }: { method: "app" | "email" }) => {
      return await requestCode(method);
    },
    onSuccess: async (data, variables) => {
      if (variables.method === "email") {
        toast.success(`Verification code sent to your email`);
      } else if (variables.method === "app") {
        dispatch(setQrCodeData(data.details));
        toast.success("Check your authenticator app for the latest code");
      }
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to send verification code");
    },
  });

  // Mutation for deactivating MFA
  const { mutate: deactivateMfaMutation, isPending: isDeactivatingMfa } = useMutation({
    mutationFn: async ({ method, code }: { method: string; code: string }) => {
      return await deactivatesMfa(method, code);
    },
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["mfa"], refetchType: "active" });
      toast.success(`${variables.method === "app" ? "Authenticator App" : "Email"} deactivated successfully`);
      navigate("/wallet/settings/two-factor-setup");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to deactivate MFA method");
    },
  });

  // Toggle two-factor method
  const toggleTwoFactorMethod = (method: "email" | "authenticator") => {
    const backendMethod = method === "authenticator" ? "app" : method;
    const isAlreadySetup = mfaDet?.results.some(m => m.name === backendMethod);
    const isPrimary = (backendMethod === "app" && selectedMethods.authenticator) || (backendMethod === "email" && selectedMethods.email);


    if (isPrimary && mfaDet?.results.length === 2) {
      toast.info("You're trying to deactivate your primary 2FA method. Please switch your primary method first.");
    } else if (isAlreadySetup) {
      const routes = {
        email: "/wallet/settings/two-factor-setup/provide-otp?currentSetup=deactive-email",
        authenticator: "/wallet/settings/two-factor-setup/provide-otp?currentSetup=deactive-app",
      };
      if (method === "email") requestOtpMutation({ method: "email" });
      navigate(routes[method]);
    } else {
      if (method === "email") {
        navigate("/wallet/settings/two-factor-setup/provide-email?currentSetup=email");
      } else if (method === "authenticator") {
        sendOtpMutation({ method: "app", payload: {} });
      } else {
        toast.error("Invalid method selected.");
      }
    }
  };

  const handleEmailSetup = () => {
    toggleTwoFactorMethod("email");
  };

  const handleAuthenticatorSetup = async () => {
    toggleTwoFactorMethod("authenticator");
  };

  const handlePrimarySetup = async (method: "app" | "email") => {
    const alreadySet =
      (method === "email" && selectedMethods.email) ||
      (method === "app" && selectedMethods.authenticator);
    const active = mfaDet?.results.flatMap((mfa) => mfa.name).includes(method);

    if (alreadySet) {
      const name = method === "email" ? "Email" : "Authenticator";
      toast.info(`${name} is already set as your primary 2FA method.`);
      return;
    }
    if (!active) {
      const name = method === "email" ? "Email" : "Authenticator";
      toast.info(`Set up ${name} as a 2FA method first.`);
      return;
    }

    if (method === "email") {
      requestOtpMutation({ method: "email" });
      navigate("/wallet/settings/two-factor-setup/provide-otp?currentSetup=emailPrimary");
    } else if (method === "app") {
      navigate("/wallet/settings/two-factor-setup/provide-otp?currentSetup=appPrimary");
    } else {
      toast.error("No valid method selected.");
    }
  };

  // Save two-factor settings
  const handleSaveTwoFactorSettings = () => {
    toast.success("Two-factor authentication settings saved!");
    navigate("/wallet/settings");
  };

  // Handle send verification code
  const handleSendVerificationCode = async () => {
    if (currentsetup === "email") {
      if (!email) {
        toast.error("Please provide a valid email address.");
        return;
      }
      sendOtpMutation({ method: "email", payload: { email } });
    } else if (currentsetup === "authenticator") {
      sendOtpMutation({ method: "app", payload: {} }); // Generate QR code
    } else {
      toast.error("Please select a two-factor method to set up.");
    }
  };

  // Mutation for verifying OTP
  const { mutate: verifyOtpMutation, isPending: isVerifyingOtp } = useMutation({
    mutationFn: async ({ method, code }: { method: string; code: string }) => {
      return await verifyOtp(method, { code });
    },
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["mfa"], refetchType: "active" });
      toast.success("OTP verified successfully!");
      navigate("/wallet/settings/two-factor-setup/enable2fa");
      dispatch(setCurrentMethod(variables.method));

    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || "Invalid OTP. Please try again.");
    },
  });

  // Reset two-factor settings
  const handleResetTwoFactorSettings = () => {
    setSelectedMethods({ email: false, authenticator: false });
    dispatch(resetTwoFactorSettings());
    toast.info("Two-factor authentication settings reset.");
  };

  // Handle OTP input
  const handleOTPChange = (value: string) => {
    dispatch(setOtp(value));
  };

  // Submit OTP for verification
  const handleOTPSubmit = async () => {
    if (!otp) {
      toast.error("OTP cannot be empty!");
      return;
    }
    if (!currentsetup) {
      toast.error("No authentication method selected!");
      return;
    }
    if (currentsetup === "email" || currentsetup === "authenticator") {
      const method = currentsetup === "email" ? "email" : "app";
      verifyOtpMutation({ method, code: otp }, { onSuccess: () => { dispatch(setOtp("")) } });
    } else if (currentsetup === "deactive-email") {
      deactivateMfaMutation({ method: "email", code: otp }, { onSuccess: () => { dispatch(setOtp("")) } });
    } else if (currentsetup === "deactive-app") {
      deactivateMfaMutation({ method: "app", code: otp }, { onSuccess: () => { dispatch(setOtp("")) } });
    } else if (currentsetup === "emailPrimary") {
      changePrimaryMfaMutation({ method: "email", code: otp }, { onSuccess: () => { dispatch(setOtp("")) } });
    } else if (currentsetup === "appPrimary") {
      changePrimaryMfaMutation({ method: "app", code: otp }, { onSuccess: () => { dispatch(setOtp("")) } });
    } else {
      toast.error("No valid method chosen");
    }
  };

  // Resend OTP code
  const handleResendCode = (mail: string) => {
    if (!currentsetup) {
      toast.error("No authentication method selected!");
      return;
    }

    // Prevent spamming with 50-second cooldown
    const now = Date.now();
    if (lastResendTime && now - lastResendTime < 50 * 1000) {
      return; // UI handles countdown
    }

    const methodMap: { [key: string]: "email" | "app" | "email-resend" } = {
      email: "email",
      "deactive-email": "email-resend",
      emailPrimary: "email-resend",
      authenticator: "app",
      "deactive-app": "app",
      appPrimary: "app",
      "code-authenticator": "app",
    };

    const method = methodMap[currentsetup];
    if (!method) {
      toast.error("Invalid authentication method!");
      return;
    }

    if (method === "email") {
      if (!mail) {
        toast.error("Email address not provided. Please restart the setup.");
        navigate("/wallet/settings/two-factor-setup/provide-email?currentSetup=email");
        return;
      }
      sendOtpMutation(
        { method: "email", payload: { email: mail } },
        {
          onSuccess: () => {
            setLastResendTime(now);
            setCooldownSeconds(50);
          },
          onError: (error: any) => {
            toast.error(error.message || "Failed to resend verification code");
          },
        }
      );
    } else if (method === "email-resend") {
      requestOtpMutation(
        { method: "email" },
        {
          onSuccess: () => {
            setLastResendTime(now);
            setCooldownSeconds(50);
          },
          onError: (error: any) => {
            toast.error(error.message || "Failed to resend verification code");
          },
        }
      );
    } else if (method === "app") {
      requestOtpMutation(
        { method: "app" },
        {
          onSuccess: () => {
            setLastResendTime(now);
            setCooldownSeconds(50);
          },
          onError: (error: any) => {
            toast.error(error.message || "Failed to request new code");
          },
        }
      );
    }
  };

  // Extract TOTP secret from QR code URI
  const getTotpSecret = () => {
    if (!qrCodeData || typeof qrCodeData !== "string") {
      return null;
    }
    try {
      const url = new URL(qrCodeData);
      const secret = url.searchParams.get("secret");
      if (!secret) {
        return null;
      }
      return secret;
    } catch (error) {
      return null;
    }
  };

  // Enable two-factor authentication
  const handle2faEnable = async () => {
    console.log(`currentStep2fa:${currentMethod}`);
    if (currentMethod === "email" || currentMethod === "app") {
      await handlePrimarySetup(currentMethod)
    } else {
      toast.error("unable to enable Authentication method")
      navigate("/wallet/settings/two-factor-setup")
    }
  };

  return {
    selectedMethods,
    email,
    otp,
    navigate,
    currentsetup,
    settingsStatus,
    handleOTPChange,
    handleOTPSubmit,
    handle2faEnable,
    handleEmailSetup,
    handleAuthenticatorSetup,
    handleSendVerificationCode,
    handleSaveTwoFactorSettings,
    handleResetTwoFactorSettings,
    toggleTwoFactorMethod,
    setEmail: (value: string) => dispatch(setEmail(value)), // Update setEmail
    isSendingOtp,
    qrCode: qrCodeData,
    mfaDet,
    isLoadingMfa,
    isDeactivatingMfa,
    handlePrimarySetup,
    isVerifyingOtp,
    isRequestingOtp,
    ischangingPrimaryMfaMutation,
    handleResendCode,
    cooldownSeconds,
    getTotpSecret,
    requestOtpMutation,
    verifyOtpMutation
  };
};

export { useWalletSettings };